import React from "react";
import { motion } from "framer-motion";
import useDocumentMeta from "../lib/useDocumentMeta";

const SECTIONS = [
    {
        title: "1. Introduction",
        body: [
            "Raj Graphics (\"we\", \"our\", \"us\") is committed to protecting the privacy of every visitor and client. This Privacy Policy explains what information we collect, how we use it, and the choices you have over your data when you interact with our website, our studio, or our services.",
        ],
    },
    {
        title: "2. Information We Collect",
        body: [
            "Information you provide: name, phone number, business name, project requirements and any details you share through our consultation forms, WhatsApp, email or in person.",
            "Project information: brand assets, briefs, references and feedback you share with us during a project engagement.",
            "Automatic information: standard usage data such as device, browser, approximate location and pages viewed, collected via cookies or similar technologies.",
        ],
    },
    {
        title: "3. How We Use Your Information",
        body: [
            "To respond to your enquiries and book strategy calls.",
            "To deliver design and signage services you have engaged us for.",
            "To send occasional service updates, project status messages and (only when you opt in) studio newsletters.",
            "To improve our website, services and customer experience.",
        ],
    },
    {
        title: "4. Sharing Your Information",
        body: [
            "We do not sell your personal data. We share information only with trusted vendors who help us deliver our services — for example, print partners, signage installers, hosting providers and payment processors — and only the minimum required to do their job.",
            "We may also disclose information when required by law, to enforce our terms, or to protect the safety and rights of others.",
        ],
    },
    {
        title: "5. Data Retention",
        body: [
            "Lead and project information is retained for as long as needed to provide our services and for legitimate business purposes such as accounting and dispute resolution. You may request deletion at any time, subject to legal record‑keeping requirements.",
        ],
    },
    {
        title: "6. Your Rights",
        body: [
            "You may request access to, correction of, or deletion of the personal data we hold about you. You may also opt out of marketing communications at any time. To exercise these rights, contact us at hello@rajgraphics.in.",
        ],
    },
    {
        title: "7. Security",
        body: [
            "We use reasonable technical and organisational measures to protect your data. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.",
        ],
    },
    {
        title: "8. Third‑Party Links",
        body: [
            "Our website may contain links to third‑party sites such as WhatsApp, social platforms or partner sites. We are not responsible for the privacy practices of those sites and encourage you to review their policies.",
        ],
    },
    {
        title: "9. Changes to this Policy",
        body: [
            "We may update this Privacy Policy from time to time. The latest version will always be available on this page with the updated effective date.",
        ],
    },
    {
        title: "10. Contact Us",
        body: [
            "Questions about this Policy? Reach out to Raj Graphics at hello@rajgraphics.in or call +91 89997 22515.",
        ],
    },
];

const PrivacyPolicyPage = () => {
    useDocumentMeta({
        title: "Privacy Policy — Raj Graphics",
        description:
            "How Raj Graphics collects, uses and protects your personal information.",
    });

    return (
        <main data-testid="privacy-page" className="bg-[#050505]">
            {/* Hero */}
            <section className="relative pt-36 pb-12 md:pt-40 md:pb-16 overflow-hidden">
                <div className="absolute inset-0 grid-pattern radial-fade opacity-60 pointer-events-none" />
                <div className="relative max-w-4xl mx-auto px-6 md:px-12">
                    <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-5">
                        Legal
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="font-poppins font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
                        data-testid="privacy-headline"
                    >
                        Privacy Policy
                    </motion.h1>
                    <p className="mt-5 font-inter text-sm text-zinc-500">
                        Effective date: 01 January 2025 · Raj Graphics, Nagpur, India
                    </p>
                </div>
            </section>

            {/* Body */}
            <section className="relative pb-24 md:pb-32 border-t border-white/5">
                <div className="max-w-4xl mx-auto px-6 md:px-12 pt-12 md:pt-16">
                    <div className="space-y-10" data-testid="privacy-sections">
                        {SECTIONS.map((s) => (
                            <article key={s.title}>
                                <h2 className="font-poppins font-medium text-white text-xl md:text-2xl mb-4">
                                    {s.title}
                                </h2>
                                <div className="space-y-4">
                                    {s.body.map((p, idx) => (
                                        <p
                                            key={idx}
                                            className="font-inter text-zinc-400 leading-relaxed"
                                        >
                                            {p}
                                        </p>
                                    ))}
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default PrivacyPolicyPage;
